import { useMemo } from 'react';
import { FieldArrayWithId } from 'react-hook-form';
import useSWR from 'swr';

import { EmpPosition, useEmployee } from '~/data/employee';
import { useAllEnums } from '~/data/enums';
import {
  getCountryByLegalEntity,
  useAllLegalEntities,
} from '~/data/legalEntity';
import { getCustomCostCenterFieldAttributes } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data';
import { IndexedCustomFields } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import { Form } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { jsEditCustomFieldData } from '~/legacy-components/CustomDataFields';

type PropsType = {
  empPositionPending?: EmpPosition;
  costCenterFields: FieldArrayWithId<
    Form,
    'schedule.0.empPosition.costCenterDetails'
  >[];
  index: number;
};

export const useCostCenterListCustomFields = (props: PropsType) => {
  const { index, costCenterFields, empPositionPending } = props;

  const { allEnums } = useAllEnums();

  const {
    legalEntities,
    isLoading: legalEntitiesLoading,
    hasData: legalEntityHasData,
  } = useAllLegalEntities();

  const {
    data: customCostCenterFieldAttributes,
    isLoading: fieldAttributesLoading,
  } = useSWR(
    'customCostCentersFieldsFieldAttributes',
    () => getCustomCostCenterFieldAttributes(),
    { revalidateOnFocus: false, refreshInterval: 0 },
  );

  const { employee } = useEmployee();
  const { empBasicData, empPosition } = employee;

  const isLoading =
    fieldAttributesLoading || (legalEntitiesLoading && !legalEntityHasData);

  const legalEntityId =
    ['NEW', 'PENDING'].includes(empBasicData.fEmpStatus) && empPositionPending
      ? empPositionPending?.fEmpLegalEntity
      : empPosition?.fEmpLegalEntity;

  const customFields = useMemo((): IndexedCustomFields => {
    if (!isLoading) {
      const hashedFields = Object.assign(
        {},
        ...customCostCenterFieldAttributes!.map((field) => ({
          [field.fCustomDataAttributeName]: field,
        })),
      );

      const legalEntityCountryForUser = getCountryByLegalEntity(
        legalEntityId,
        legalEntities,
      );

      return costCenterFields.reduce((prevValue, curValue, ccIndex) => {
        return {
          ...prevValue,
          [curValue.costCenterId]: jsEditCustomFieldData({
            strEntity: 'COST_CENTER',
            customFieldAttributesHash: hashedFields,
            legalEntityCountry: legalEntityCountryForUser,
            customNamePrefix: `schedule.${index}.empPosition.costCenterDetails.${ccIndex}.customData.`,
            allEnums,
          }),
        };
      }, {});
    }
    return {};
  }, [
    costCenterFields,
    allEnums,
    customCostCenterFieldAttributes,
    index,
    isLoading,
    legalEntities,
    legalEntityId,
  ]);

  return {
    customFields,
    isLoading,
  };
};
